import React from "react";

import { Controller, UseFormReturn } from "react-hook-form";

import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { FormItem } from "src/base-components/FormItem";
import { Switch } from "src/base-components/Switch";
import { Callout } from "src/design-system/Callout";
import { TextWithMarkdownLinks } from "src/integrationNode/editorComponents/TextWithMarkdownLinks";
import {
  DEFAULT_OUTPUT_BE_NAME,
  OutputMappings,
} from "src/integrationNode/types";

type Props = {
  displayName: string;
  description?: string;
  formProps: UseFormReturn<OutputMappings>;
  // To be able to use the typed react hook forms controller in this component, we have to specify
  // which fields the component excepts
  fieldName: `${typeof DEFAULT_OUTPUT_BE_NAME}` | `insights.${string}`;
  runFieldError: string | undefined;
  immutable: boolean;
  hint?: string | React.ReactNode;
  type?: string | null;
  deprecationText?: string;
};

export const InsightMappingRow: React.FC<Props> = ({
  displayName,
  description,
  formProps,
  fieldName,
  runFieldError,
  immutable,
  hint,
  type,
  deprecationText,
}) => {
  const insightName = fieldName.split(".")[1];

  if (typeof hint == "string") {
    hint = hint.split("\n").map((line, i) => (
      <p key={i}>
        <TextWithMarkdownLinks text={line} type="tooltip" />
      </p>
    ));
  }
  let tooltipContent: React.ReactNode = null;

  if (hint || type) {
    tooltipContent = (
      <div className="space-y-2 font-fira-code">
        {hint && (
          <p className="font-inter-normal-12px">
            {typeof hint === "string"
              ? hint.split("\n").map((line, i) => (
                  <p key={i}>
                    <TextWithMarkdownLinks text={line} type="tooltip" />
                  </p>
                ))
              : hint}
          </p>
        )}
        {type && (
          <p className="font-bold">
            Type: <span className="font-normal">{type}</span>
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="mb-7" data-loc={`insight-mapping-row-${insightName}`}>
      <div className="mb-2.5 flex items-center justify-between">
        <div>
          <h3
            className="font-inter-medium-12px text-gray-800"
            data-loc={`insight-mapping-row-${insightName}-name`}
          >
            {displayName}
            <FormItem.Help body={tooltipContent} />
          </h3>
          {description && (
            <span className="font-inter-normal-12px text-gray-500">
              <TextWithMarkdownLinks text={description} />
            </span>
          )}
        </div>
        <Controller
          control={formProps.control}
          name={`${fieldName}.selected`}
          render={(props) => (
            <Switch
              disabled={immutable}
              enabled={props.field.value}
              onChange={() => {
                props.field.onChange(!props.field.value);
              }}
            />
          )}
        />
      </div>
      {formProps.getValues(fieldName).selected && (
        <>
          <div className="mb-2 flex w-full flex-row items-center rounded-lg bg-gray-50 px-5 py-4">
            <div className="mr-2 flex font-inter-normal-12px text-gray-700">
              Output location
            </div>
            <Controller
              control={formProps.control}
              name={`${fieldName}.assignedTo`}
              render={({ field }) => (
                <AutocompleteCodeInput
                  disabled={immutable}
                  error={runFieldError}
                  prefix="data."
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          {deprecationText && (
            <div className="my-2">
              <Callout type="warning">{deprecationText}</Callout>
            </div>
          )}
        </>
      )}
    </div>
  );
};
